// =====================================================================
// src/modulos/escalas.js — a escala da governança (supervisão/administração)
//
// Duas partes na mesma tela:
//   1. O DIA: a escala resolvida de uma data (padrão − folgas + coberturas),
//      por andar, volantes no topo; as exceções da data, com "Folga" e
//      "Cobertura" para registrar novas.
//   2. O PADRÃO SEMANAL: a grade andar × dia da semana (com a linha
//      "Volantes"), cada célula com as pessoas e a faixa; "+" escala, "×"
//      tira. O que vale toda semana até mudar.
//   3. TROCAS FUTURAS (0068): a carga de limpeza dos próximos dias, para
//      decidir folgas — "deveria estar na mesma página do escala e folgas"
//      (Hugo, 27/09). Os dados vêm de `trocas_da_escala`: quarto e datas,
//      o hóspede só para quem tem Hóspedes.
//
// Tudo que é regra está no banco (0009/0010): a tela só chama escalar,
// desescalar, registrar_excecao, remover_excecao e mostra o que escala(data)
// devolve. A camareira não chega aqui (não tem 'escalas'); quem só VÊ o
// módulo enxerga tudo sem os botões.
// =====================================================================
import { hojeDe } from '../lib/contexto.js';
import { telaDeTrocas } from './trocas.js';
import { el, icone, abrirModal, fecharModal, aviso, confirmar, enquantoAtualiza } from '../ui.js';
import {
  DIAS, DIAS_CURTOS, dataISO, diaDaSemana, somarDias, dataCurta, andaresDoHotel, grade, nomeComFaixa,
  quartosDoAndar, disponiveis, excecoesDoDia, descreverExcecao, escalaPorAndar, VOLANTE,
} from '../lib/escalas.js';

export function montar(cont, { auth, contexto, agora = () => Date.now() }) {
  const nivel = (contexto.modulos.find(m => m.chave === 'escalas') || {}).nivel;
  const podeEditar = nivel === 'editar';
  let vivo = true;
  let data = hojeDe(contexto, agora());   // o dia do hotel (0020)
  let quadro = [], padrao = [], excecoes = [], equipe = [], escalaDia = [];
  // O Administrador do Sistema não tem hotel: nas trocas ele escolhe qual
  // olhar, como na Lista de Reservas.
  const sistema = !!(contexto.usuario && contexto.usuario.sistema);
  let clientes = [], clienteSel = sistema ? null : (contexto.cliente && contexto.cliente.id);
  const trocas = telaDeTrocas({
    id: 'esc-trocas',
    hoje: () => hojeDe(contexto, agora()),
    carregar: async (ini, fim) => {
      if (sistema && !clienteSel) {
        clientes = await auth.ler('clientes', 'select=id,nome&order=nome');
        clienteSel = clientes.length ? clientes[0].id : null;
      }
      if (!clienteSel) return [];
      const linhas = await auth.rpc('trocas_da_escala', { p_ini: ini, p_fim: fim, ...(sistema ? { p_cliente: clienteSel } : {}) });
      // a forma que lib/trocas.js conta: situação já filtrada no banco
      return linhas.map(t => ({ id: t.reserva_id, unidade_id: t.unidade_id, quarto: t.quarto, check_in: t.check_in, check_out: t.check_out, situacao: 'confirmada', hospede: t.hospede }));
    },
    hospedeDe: r => r.hospede || null,
    comTelefone: false,
    comPlataforma: false,
    extraDaNavegacao: () => sistema ? el('select', { class: 'input', id: 'tr-cliente', style: { minWidth: '160px', padding: '7px 10px', fontSize: '13px', minHeight: '38px' }, onchange: ev => { clienteSel = ev.target.value; trocas.recarregar(); } },
      clientes.map(c => el('option', { value: c.id, selected: c.id === clienteSel }, c.nome))) : null,
  });

  async function carregar() {
    const [q, p, e, eq, esc] = await Promise.all([
      auth.ler('v_quadro_governanca', 'select=unidade_id,numero,andar'),
      auth.ler('escalas_padrao', 'select=*'),
      auth.ler('escalas_excecoes', `select=*&data=eq.${data}`),
      auth.rpc('equipe_de_governanca', {}),
      auth.rpc('escala', { p_data: data }),
    ]);
    quadro = q; padrao = p; excecoes = e; equipe = eq; escalaDia = esc;
  }
  // enquantoAtualiza: o <html> conta os ciclos, e a prova espera o contador
  // crescer em vez de adivinhar que a tela ja se refez (ui.js).
  function recarregar() {
    return enquantoAtualiza(async () => {
      try { await carregar(); } catch (e) { aviso(e.message, 'erro'); return; }
      desenhar();
    });
  }

  function desenhar() {
    if (!vivo) return;
    cont.replaceChildren();
    cont.dataset.tela = 'escalas';
    desenharDia();
    desenharPadrao();
    // 3. as trocas: a mesma tela de sempre, logo abaixo do padrão semanal
    cont.appendChild(el('div', { style: { marginTop: '30px', borderTop: '1px solid #e5e7eb', paddingTop: '20px' } }, trocas.elemento));
    if (!trocas.carregada()) trocas.recarregar();
  }

  // ── 1. o dia ───────────────────────────────────────────────────────
  function desenharDia() {
    const hoje = hojeDe(contexto, agora());
    cont.appendChild(el('div', { class: 'gov-cabeca' },
      el('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' } },
        el('button', { type: 'button', class: 'btn btn-secondary btn-sm', id: 'dia-anterior', 'aria-label': 'Dia anterior', onclick: () => { data = somarDias(data, -1); recarregar(); } }, '‹'),
        el('input', { type: 'date', class: 'input', id: 'dia', value: data, style: { width: 'auto', fontSize: '14px', padding: '7px 10px' },
          onchange: ev => { if (ev.target.value) { data = ev.target.value; recarregar(); } } }),
        el('button', { type: 'button', class: 'btn btn-secondary btn-sm', id: 'dia-seguinte', 'aria-label': 'Dia seguinte', onclick: () => { data = somarDias(data, 1); recarregar(); } }, '›'),
        data !== hoje ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', onclick: () => { data = hoje; recarregar(); } }, 'Hoje') : null,
        el('span', { style: { fontSize: '13px', color: '#6b7280' }, id: 'dia-nome' }, `${DIAS[diaDaSemana(data)]}${data === hoje ? ' · hoje' : ''}`)),
      podeEditar ? el('div', { style: { display: 'flex', gap: '8px' } },
        el('button', { type: 'button', class: 'btn btn-secondary btn-sm', id: 'btn-folga', onclick: () => modalExcecao('folga') }, 'Folga / falta'),
        el('button', { type: 'button', class: 'btn btn-primary btn-sm', id: 'btn-cobertura', onclick: () => modalExcecao('cobertura') }, icone('ic-plus'), 'Cobertura')) : null));

    cont.appendChild(el('div', { class: 'secao-titulo' }, `Escala de ${dataCurta(data)}`));
    const porAndar = escalaPorAndar(escalaDia);
    if (!porAndar.length) cont.appendChild(el('p', { class: 'vazio', id: 'escala-vazia' }, 'Ninguém escalado neste dia. Monte o padrão semanal abaixo ou registre uma cobertura.'));
    const lista = el('div', { id: 'escala-dia' });
    for (const g of porAndar) {
      lista.appendChild(el('div', { class: 'quadro-linha', style: { borderLeftColor: g.andar === null ? '#7c3aed' : '#1B3A7A', background: '#f9fafb' }, dataset: { andar: g.andar === null ? 'volante' : String(g.andar) } },
        el('span', { class: 'n' }, g.rotulo),
        el('span', null, g.pessoas.map(p => el('span', { class: 'escala-nome' + (p.origem === 'cobertura' ? ' cobertura' : ''), title: p.origem === 'cobertura' ? 'cobertura de hoje' : 'padrão semanal' }, nomeComFaixa(p))))));
    }
    cont.appendChild(lista);

    const exc = excecoesDoDia(excecoes, data, equipe);
    if (exc.length) {
      cont.appendChild(el('div', { class: 'secao-titulo' }, 'Exceções do dia'));
      const ul = el('div', { id: 'excecoes' });
      for (const x of exc) {
        ul.appendChild(el('div', { class: 'excecao ' + x.tipo, dataset: { excecao: x.id } },
          icone(x.tipo === 'folga' ? 'ic-x-circle' : 'ic-plus'), descreverExcecao(x),
          podeEditar ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm x', 'aria-label': 'Remover', onclick: async () => {
            try { await auth.rpc('remover_excecao', { p_id: x.id }); } catch (e) { aviso(e.message, 'erro'); return; }
            recarregar();
          } }, icone('ic-trash')) : null));
      }
      cont.appendChild(ul);
    }
  }

  function seletorPessoa(id, lista) {
    return el('select', { class: 'input', id }, lista.map(p => el('option', { value: p.id }, p.nome)));
  }
  function seletorAndar(id, comVolante) {
    const andares = andaresDoHotel(quadro);
    return el('select', { class: 'input', id },
      comVolante ? el('option', { value: '' }, comVolante) : null,
      andares.map(a => el('option', { value: String(a) }, `${a}º andar`)));
  }
  function camposFaixa(andarSel) {
    const de = el('select', { class: 'input', id: 'faixa-de' }, el('option', { value: '' }, '— andar inteiro —'));
    const ate = el('select', { class: 'input', id: 'faixa-ate' }, el('option', { value: '' }, '—'));
    const encher = () => {
      const andar = andarSel.value === '' ? null : Number(andarSel.value);
      const quartos = andar === null ? [] : quartosDoAndar(quadro, andar);
      de.replaceChildren(el('option', { value: '' }, andar === null ? '— volante não tem faixa —' : '— andar inteiro —'), ...quartos.map(n => el('option', { value: n }, n)));
      ate.replaceChildren(el('option', { value: '' }, '—'), ...quartos.map(n => el('option', { value: n }, n)));
      de.disabled = ate.disabled = andar === null;
    };
    andarSel.addEventListener('change', encher); encher();
    const wrap = el('div', { class: 'form-row' }, el('label', { class: 'label' }, 'Faixa de quartos (opcional)'),
      el('div', { style: { display: 'flex', gap: '8px', alignItems: 'center' } }, de, el('span', null, 'até'), ate));
    return { wrap, de, ate };
  }

  function modalExcecao(tipo) {
    if (!equipe.length) { aviso('Ninguém para escalar: cadastre a equipe de governança primeiro.', 'erro'); return; }
    const pessoa = seletorPessoa('exc-pessoa', equipe);
    const andar = seletorAndar('exc-andar', tipo === 'folga' ? 'O dia inteiro' : 'Como volante');
    const faixa = tipo === 'cobertura' ? camposFaixa(andar) : null;
    const motivo = el('input', { class: 'input', id: 'exc-motivo', placeholder: tipo === 'folga' ? 'Ex.: atestado, folga combinada' : 'Ex.: cobrindo a Ana' });
    const erro = el('div', { class: 'alert alert-error hidden', id: 'exc-erro' });
    abrirModal([
      el('h2', { class: 'modal-title' }, tipo === 'folga' ? `Folga ou falta em ${dataCurta(data)}` : `Cobertura em ${dataCurta(data)}`),
      el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'exc-pessoa' }, 'Quem'), pessoa),
      el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'exc-andar' }, tipo === 'folga' ? 'De onde sai' : 'Onde entra'), andar),
      faixa ? faixa.wrap : null,
      el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'exc-motivo' }, 'Motivo (opcional)'), motivo),
      erro,
      el('div', { style: { display: 'flex', gap: '10px', marginTop: '14px' } },
        el('button', { type: 'button', class: 'btn btn-primary', style: { flex: '1' }, id: 'exc-salvar', onclick: async () => {
          erro.classList.add('hidden');
          try {
            await auth.rpc('registrar_excecao', {
              p_data: data, p_usuario: pessoa.value, p_andar: andar.value === '' ? null : Number(andar.value), p_tipo: tipo, p_motivo: motivo.value || null,
              p_quarto_de: faixa && faixa.de.value ? faixa.de.value : null, p_quarto_ate: faixa && faixa.ate.value ? faixa.ate.value : null,
            });
          } catch (e) { erro.textContent = e.message; erro.classList.remove('hidden'); return; }
          fecharModal(); recarregar();
        } }, 'Registrar'),
        el('button', { type: 'button', class: 'btn btn-secondary', style: { flex: '1' }, onclick: fecharModal }, 'Voltar')),
    ]);
  }

  // ── 2. o padrão semanal ────────────────────────────────────────────
  function desenharPadrao() {
    cont.appendChild(el('div', { class: 'secao-titulo', style: { marginTop: '26px' } }, 'Padrão semanal'));
    cont.appendChild(el('div', { style: { fontSize: '13px', color: '#6b7280', marginBottom: '10px' } },
      'Vale toda semana até mudar. Quem tem faixa vê só a faixa; quem está em "Volantes" vê o hotel inteiro. Folgas e coberturas de um dia ficam acima, na data.'));
    const andares = andaresDoHotel(quadro);
    if (!andares.length) { cont.appendChild(el('p', { class: 'vazio' }, 'Este hotel ainda não tem quartos com andar cadastrado.')); return; }
    const diaHoje = diaDaSemana(data);
    const tabela = el('table', { class: 'escala-grade', id: 'padrao' },
      el('thead', null, el('tr', null, el('th', null, 'Andar'), DIAS_CURTOS.map((d, i) => el('th', { class: i === diaHoje ? 'hoje' : '' }, d)))),
      el('tbody', null, grade(padrao, andares, equipe).map(linha =>
        el('tr', { dataset: { andar: linha.andar === VOLANTE ? 'volante' : String(linha.andar) } },
          el('td', { class: 'andar' }, linha.rotulo),
          linha.dias.map((celula, dia) => el('td', { class: dia === diaHoje ? 'hoje' : '', dataset: { dia: String(dia) } },
            celula.map(p => el('span', { class: 'escala-nome', dataset: { usuario: p.usuario_id } }, nomeComFaixa(p),
              podeEditar ? el('button', { type: 'button', 'aria-label': `Tirar ${p.nome}`, onclick: async () => {
                const ok = await confirmar({ titulo: `Tirar ${p.nome}?`, texto: `${linha.rotulo}, toda ${DIAS[dia].toLowerCase()}.`, sim: 'Tirar', nao: 'Voltar', perigoso: true });
                if (!ok) return;
                try { await auth.rpc('desescalar', { p_id: p.id }); } catch (e) { aviso(e.message, 'erro'); return; }
                recarregar();
              } }, '×') : null)),
            podeEditar && disponiveis(celula, equipe).length
              ? el('button', { type: 'button', class: 'escala-add', 'aria-label': `Escalar em ${linha.rotulo}, ${DIAS[dia]}`, onclick: () => modalEscalar(linha.andar, dia, celula) }, '+')
              : null))))));
    cont.appendChild(el('div', { style: { overflowX: 'auto' } }, tabela));
  }

  function modalEscalar(andar, dia, celula) {
    const pessoa = seletorPessoa('esc-pessoa', disponiveis(celula, equipe));
    const andarSel = el('select', { class: 'input', id: 'esc-andar', disabled: true }, el('option', { value: andar === VOLANTE ? '' : String(andar) }, andar === VOLANTE ? 'Volante' : `${andar}º andar`));
    const faixa = andar === VOLANTE ? null : camposFaixa(andarSel);
    const erro = el('div', { class: 'alert alert-error hidden', id: 'esc-erro' });
    abrirModal([
      el('h2', { class: 'modal-title' }, `Escalar — ${andar === VOLANTE ? 'Volantes' : andar + 'º andar'}, toda ${DIAS[dia].toLowerCase()}`),
      el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'esc-pessoa' }, 'Quem'), pessoa),
      faixa ? faixa.wrap : el('div', { style: { fontSize: '13px', color: '#6b7280', marginBottom: '10px' } }, 'Volante vê o hotel inteiro, andares com mais necessidade primeiro, e pode marcar "cama forrada".'),
      erro,
      el('div', { style: { display: 'flex', gap: '10px', marginTop: '14px' } },
        el('button', { type: 'button', class: 'btn btn-primary', style: { flex: '1' }, id: 'esc-salvar', onclick: async () => {
          erro.classList.add('hidden');
          try {
            await auth.rpc('escalar', { p_dia_semana: dia, p_andar: andar === VOLANTE ? null : andar, p_usuario: pessoa.value,
              p_quarto_de: faixa && faixa.de.value ? faixa.de.value : null, p_quarto_ate: faixa && faixa.ate.value ? faixa.ate.value : null });
          } catch (e) { erro.textContent = e.message; erro.classList.remove('hidden'); return; }
          fecharModal(); recarregar();
        } }, 'Escalar'),
        el('button', { type: 'button', class: 'btn btn-secondary', style: { flex: '1' }, onclick: fecharModal }, 'Voltar')),
    ]);
  }

  cont.replaceChildren(el('p', { class: 'vazio' }, 'Carregando a escala…'));
  // a carga inicial conta como ciclo: a prova espera a PRIMEIRA pintura
  // pelo mesmo contador que usa para todas as outras.
  enquantoAtualiza(() => carregar().then(() => { if (vivo) desenhar(); }))
    .catch(e => { if (vivo) cont.replaceChildren(el('div', { class: 'alert alert-error' }, 'Não foi possível carregar a escala: ', e.message)); });

  return function desmontar() { vivo = false; fecharModal(); };
}
